import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import API_BASE_URL from "./config";
import "./Login.css";

const SuperAdminLogin = () => {
  const { login, logout, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [credentials, setCredentials] = useState({
    email: "",
    password: ""
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Check for success message from signup
  const successMessage = location.state?.message;

  // If user is already logged in as super admin, redirect to panel
  // If logged in as org admin, log them out to ensure separation
  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.role === 'super_admin') {
        navigate("/super-admin", { replace: true });
      } else if (user.role === 'org_admin') {
        // User is logged in as org_admin but trying to access super admin login
        // Log them out to ensure complete separation
        logout();
      }
    }
  }, [isAuthenticated, user, navigate, logout]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    setError(""); // Clear error when user starts typing
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/super-admin/login`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password
        }),
      });

      const data = await response.json();

      if (response.ok && data.token) {
        // Success - login the user with JWT token
        login(data.token, data.user);
        
        // Redirect to super admin panel
        navigate("/super-admin");
      } else {
        setError(data.message || data.error || "Invalid email or password");
      }
    } catch (error) {
      setError("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="auth-illustration">
        <div className="brand">
          <img className="brand-logo-img" src={`${process.env.PUBLIC_URL || ''}/assets/wetano-logo.png`} alt="WeTano Solutions" />
          <div className="brand-text">
            <h2>WeTano Solutions</h2>
            <p>Super Admin Console</p>
          </div>
        </div>
        <ul className="highlights">
          <li>Manage organizations and domains</li>
          <li>Assign user quotas (0-100)</li>
          <li>Monitor system-wide usage</li>
        </ul>
      </div>

      <div className="login-card">
        <div className="brand-mini">
          <img src={`${process.env.PUBLIC_URL || ''}/assets/wetano-logo.png`} alt="WeTano Solutions" />
          <span>WeTano Solutions</span>
        </div>
        <div className="login-header">
          <h1>Super Admin Login</h1>
          <p>Sign in to manage organizations</p>
          {successMessage && (
            <div className="success-message">{successMessage}</div>
          )}
          {isAuthenticated && user && user.role !== 'super_admin' && (
            <div className="info-message">
              You are currently logged in as Organization Admin. Please log out first to access Super Admin panel.
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={credentials.email}
              onChange={handleChange}
              placeholder="admin@example.com"
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label htmlFor="password">Password</label>
              <Link
                to="/super-admin-forgot-password"
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#3b82f6',
                  fontSize: '12px',
                  cursor: 'pointer',
                  padding: 0,
                  textDecoration: 'none'
                }}
              >
                Forgot password?
              </Link>
            </div>
            <input
              type="password"
              id="password"
              name="password"
              value={credentials.password}
              onChange={handleChange}
              placeholder="Enter your password"
              required
              disabled={isLoading}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            type="submit"
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div className="login-footer">
          <p>
            Need an account? <Link to="/super-admin-signup">Create Super Admin Account</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminLogin;

